import React, { useCallback } from 'react';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import type {
	ApplicationLinkManifest,
	OutboundConnection,
} from './CommonApplicationLinkCreationEntity.tsx';
import { CreateFlowDialog } from './CreateFlowDialog.tsx';
import { fetchApplinkManifestForUrl, fetchThisManifest } from './fetchApplinkManifestForUrl.tsx';
import type { StageReviewProps } from './StageProps.types.tsx';

type CreateLinkComponentProps = {
	isOpen: boolean;
	onClose: () => void;
	onCloseComplete?: () => void;
	targetUrl: string;
	availableConnections?: OutboundConnection[];
	localManifest?: ApplicationLinkManifest;
	remoteManifest?: ApplicationLinkManifest;
};

const CreateOAuth2Drawer = (props: CreateLinkComponentProps): React.JSX.Element => {
	const fetchManifestsPromise = useCallback(async () => {
		try {
			const localManifest = props.localManifest ?? (await fetchThisManifest());
			const remoteManifest =
				props.remoteManifest ?? (await fetchApplinkManifestForUrl(props.targetUrl));

			const newProps: StageReviewProps = {
				kind: 'review',
				variant: 'OAUTH2.0',
				localManifest,
				remoteManifest,
				rpcUrl: props.targetUrl,
			};
			return newProps;
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.unsafeErrorWithCustomerData(
				'applinks.administration.hybrid.create-oauth2-drawer.fetchManifests',
				'Failed to retrieve manifests (as part of portalled modal load)',
				maybeError,
			);

			throw err;
		}
	}, [props.targetUrl, props.localManifest, props.remoteManifest]);

	return (
		<CreateFlowDialog
			isOpen={props.isOpen}
			onExit={props.onClose}
			onCloseComplete={props.onCloseComplete}
			availableConnections={props.availableConnections}
			startingStage={{
				kind: 'loading',
				promise: fetchManifestsPromise(),
			}}
		/>
	);
};

export default CreateOAuth2Drawer;
